import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useVoice } from "../../../contexts/VoiceContext";
import Lottie from "lottie-react";
import Button from "../../common/Button";
import editIcon from "../../../assets/dashboard/edit-icon.svg";
import copy from "../../../assets/agent/copy.svg";
import add from "../../../assets/agent/addContact.svg";
import discart from "../../../assets/agent/discartContact.svg";
import success from "../../../assets/agent/success.svg";
import fail from "../../../assets/agent/fail.svg";
import loadingAnim from "../../../assets/common/json-Animacin-carga-.json";
import CreateContactModal from "./CreateContactModal";
import DeleteModal from "./DeleteContactModal";
import Info from "./Info";
import LastNote from "./LastNote";
import { useContactEditSubmit } from "../mobile/useContactEditSubmit";

const ContactInfo = ({ contact, clientDetails, callType }) => {
  const { makeCall, status } = useVoice();
  const [showModal, setShowModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const prevVoiceStatusRef = useRef(status);

  const { t } = useTranslation();

  const {
    editMode,
    setEditMode,
    contactData,
    editedData,
    isChanged,
    errors,
    loading,
    successModal,
    errorModal,
    requiredFields,
    channelOptions,
    sectorOptions,
    handleChange,
    handleEditClick,
    handleSave,
    handleModal,
  } = useContactEditSubmit({ contact });

  const togglePanel = (panel) => {
    setActivePanel((prev) => (prev === panel ? null : panel));
  };

  const showToastFor = (message, durationMs = 2000) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), durationMs);
  };

  useEffect(() => {
    const prev = prevVoiceStatusRef.current;
    if (prev === status) return;

    if (status === "dialing") showToastFor("Dialing...");
    if (status === "ringing") showToastFor("Ringing...");
    if (status === "calling") showToastFor("Connected");

    if (
      status === "ready" &&
      (prev === "calling" || prev === "ringing" || prev === "dialing")
    ) {
      showToastFor("Call ended");
    }

    prevVoiceStatusRef.current = status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToastFor("Copied!");
    });
  };

  const renderField = (label, field, isCopyable = false) => (
    <div className="mb-2.5">
      <div className="flex space-x-2 items-center">
        <p className="text-gray-500 text-xs mb-0.5">
          {label}
          {requiredFields.includes(field) && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </p>
        {isCopyable && contactData[field] && (
          <div className="flex items-center gap-1.5">
            <span
              className="flex items-center justify-center cursor-pointer hover:opacity-80 flex-shrink-0"
              onClick={() => handleCopy(contactData[field])}
              title="Copy"
            >
              <img src={copy} alt="copy" className="w-3.5 h-3.5" />
            </span>
            {(field === "phone" ||
              field === "cellphone" ||
              field === "mobile") &&
              status !== "disabled" &&
              status !== "unsupported" && (
                <span
                  className="flex items-center justify-center cursor-pointer hover:opacity-80 flex-shrink-0 bg-green-100 p-1 rounded-full"
                  onClick={async () => {
                    try {
                      console.log("Attempting to call:", contactData[field]);
                      await makeCall({ params: { To: contactData[field] } });
                    } catch (err) {
                      console.error("Call failed:", err);
                    
                      const message =
                        err instanceof Error
                          ? err.message
                          : err?.message || "Unexpected voice error";
                    
                      setToastMessage("Call Failed: " + message);
                    }
                  }}
                  title="Call"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3.5 h-3.5 text-green-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </span>
              )}
          </div>
        )}
      </div>
      {editMode ? (
        field === "channel" ? (
          <select
            value={editedData[field] || ""}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-full text-sm p-1.5 border border-gray-300 rounded focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">{t("selectChannel")}</option>
            {channelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : field === "industry" ? (
          <select
            value={editedData[field] || ""}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-full text-sm p-1.5 border border-gray-300 rounded focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">{t("selectSector")}</option>
            {sectorOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={editedData[field] || ""}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-full text-sm p-1.5 border border-gray-300 rounded focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        )
      ) : (
        <p className="text-sm font-medium text-primary break-words">
          {contactData[field] || "-"}
        </p>
      )}
      {errors[field] && (
        <p className="text-red-500 text-xs mt-1">{errors[field]}</p>
      )}
    </div>
  );

  return (
    <div className="relative">
      <CreateContactModal
        open={showModal}
        onClose={() => setShowModal(false)}
        contact={contactData}
      />
      <DeleteModal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        contact={contactData}
      />

      <div className="p-4 bg-white rounded-lg shadow-md w-full h-auto relative">
        <div className="flex justify-between items-center flex-wrap gap-2 mb-3">
          <h2 className="text-base font-semibold text-primary">
            {t("agent.contact.info")}
          </h2>
          <div className="flex items-center gap-1.5">
            <Info
              clientDetails={clientDetails}
              isOpen={activePanel === "info"}
              onToggle={() => togglePanel("info")}
            />
            <LastNote
              contact={contactData}
              callType={callType}
              isOpen={activePanel === "note"}
              onToggle={() => togglePanel("note")}
            />
            <button
              className="w-7 h-7 flex items-center justify-center rounded-full bg-green-50"
              onClick={() => setShowModal(true)}
              title="Add contact"
            >
              <img src={add} alt="add" className="w-3.5 h-3.5" />
            </button>
            <button
              className="w-7 h-7 flex items-center justify-center rounded-full bg-red-50"
              onClick={() => setDeleteModal(true)}
              title="Discard contact"
            >
              <img src={discart} alt="discart" className="w-3.5 h-3.5" />
            </button>
            <span
              className="group w-7 h-7 flex items-center justify-center rounded-full bg-lightBlue hover:bg-[linear-gradient(-80deg,#136BF0_0%,#0C49A6_100%)] cursor-pointer"
              style={{ position: "relative" }}
              onClick={handleEditClick}
            >
              <img
                src={editIcon}
                alt="editIcon"
                className="w-3.5 h-4 transition duration-300 group-hover:brightness-0 group-hover:invert"
              />
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          {renderField(`${t("confirm.table.name")}`, "firstName")}
          {renderField(`${t("confirm.table.lastName")}`, "lastName")}
          {renderField(`${t("dashboard.table.company")}`, "company")}
          {renderField(`${t("admin.role")}`, "role")}
          {renderField("Sector", "industry")}
          {renderField(`${t("admin.phone")}`, "phone", true)}
          {renderField(`${t("confirm.table.mobile")}`, "cellphone", true)}
          {renderField(`${t("email")}`, "email")}
          {renderField(`${t("confirm.table.country")}`, "country")}
          {renderField(`${t("confirm.table.city")}`, "city")}
          {renderField(`${t("confirm.table.website")}`, "website")}
          {renderField(`${t("agent.Channel")}`, "channel")}
        </div>

        {editMode && (
          <div className="mt-4 flex items-center justify-center gap-6">
            <Button
              onClick={() => setEditMode(!editMode)}
              className="w-full sm:w-40 px-4 py-1.5 text-sm"
              variant="secondary"
            >
              {t("button.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              className="w-full sm:w-40 px-4 py-1.5 text-sm"
              variant="primary"
              disabled={loading}
            >
              {t("button.save")}
            </Button>
          </div>
        )}
      </div>
      {showToast && (
        <div
          className='fixed right-4 z-[60] text-white p-2 rounded shadow-lg'
        >
          {toastMessage}
        </div>
      )}
      {loading ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-secondary text-xl shadow-xl p-6 relative flex flex-col items-center">
            <Lottie
              animationData={loadingAnim}
              loop
              autoplay
              style={{ height: "5rem", width: "5rem" }}
            />
          </div>
        </div>
      ) : (
        ""
      )}
      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img
              src={success}
              alt="success"
              className="w-[60px] h-[60px] mb-4"
            />
            <p className="mb-4 text-xl font-bold">
              {t("admin.contactSave.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl w-[455px] text-center">
              {t("admin.contactSave.desc")}
            </p>
          </div>
        </div>
      )}
      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("admin.userError.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            {errors.submit && (
              <p className="mb-6 text-xl w-[455px] text-center">
                {errors.submit}
              </p>
            )}
            <Button variant="secondary" onClick={handleModal}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactInfo;